import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  apiErrorText,
  useCreateRegisterMutation,
  useDeleteRegisterMutation,
  useGetRegisterQuery,
  useLazyExportRegisterXlsxQuery,
  useListRegistersQuery,
} from '@shared/api';
import type { RegisterEntry } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Input, Label, Field } from '@shared/ui/Input';
import { useAgentPermission } from '@entities/user';
import { useAppSelector } from '@app/hooks';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import {
  fmtMoney,
  fmtNum,
} from '@shared/lib/smetaLabels';
import { ParseWarningsBanner } from './ParseWarningsBanner';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const CheckRow = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  font: inherit;
  color: inherit;
  transition: border-color 120ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const ObjName = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Muted = styled.div<{ $mt?: number }>`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  ${({ $mt }) => ($mt !== undefined ? `margin-top: ${$mt}px;` : '')}
`;

const SubmitWrap = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
  }

  th.num {
    text-align: right;
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const TableTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing(5)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const FileHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Err = styled.div<{ $mt?: number }>`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  ${({ $mt }) => ($mt !== undefined ? `margin-top: ${$mt}px;` : '')}
`;

const CrossLink = styled(Link)`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primary};
`;

const FailedSourcesBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  border-left-width: 4px;
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
  line-height: 1.5;
`;

const FailedSourcesTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.status.warning};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const FailedSourcesList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(4)};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function extractFailedSources(summary: unknown): {
  failedSources: string[];
  totalFiles: number | null;
} {
  if (!summary || typeof summary !== 'object') {
    return { failedSources: [], totalFiles: null };
  }
  const rec = summary as Record<string, unknown>;
  const rawList = Array.isArray(rec.failed_sources) ? rec.failed_sources : [];
  const failedSources = rawList
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map((v) => v.trim());
  const totalFiles =
    typeof rec.files === 'number' && Number.isFinite(rec.files) ? rec.files : null;
  return { failedSources, totalFiles };
}

function statusTone(status: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'processing':
      return 'info';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'готово';
    case 'processing':
      return 'обработка…';
    case 'pending':
      return 'в очереди';
    case 'failed':
      return 'ошибка';
    default:
      return status;
  }
}

function InvoiceTable({ entries }: { entries: RegisterEntry[] }) {
  if (entries.length === 0) {
    return <Muted $mt={8}>Позиции накладных не загружены.</Muted>;
  }
  return (
    <Table>
      <thead>
        <tr>
          <th>Месяц</th>
          <th>№ накл.</th>
          <th>Дата</th>
          <th>Поставщик</th>
          <th>Материал</th>
          <th>Ед.</th>
          <th className="num">Кол-во</th>
          <th className="num">Сумма</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id}>
            <td>{e.document_month ?? '—'}</td>
            <td>{e.document_no ?? '—'}</td>
            <td>{e.document_date ?? '—'}</td>
            <td>{e.supplier ?? '—'}</td>
            <td>{e.name}</td>
            <td>{e.unit ?? '—'}</td>
            <td className="num">{fmtNum(e.quantity)}</td>
            <td className="num">{fmtMoney(e.total_cost)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function ActTable({ entries }: { entries: RegisterEntry[] }) {
  if (entries.length === 0) {
    return <Muted $mt={8}>Позиции актов КС-2 не загружены.</Muted>;
  }
  return (
    <Table>
      <thead>
        <tr>
          <th>Месяц</th>
          <th>Участок</th>
          <th>№ акта</th>
          <th>Наименование работ</th>
          <th>Ед.</th>
          <th className="num">Кол-во</th>
          <th className="num">Сумма</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id}>
            <td>{e.document_month ?? '—'}</td>
            <td>{e.uchastok ?? '—'}</td>
            <td>{e.document_no ?? '—'}</td>
            <td>{e.name}</td>
            <td>{e.unit ?? '—'}</td>
            <td className="num">{fmtNum(e.quantity)}</td>
            <td className="num">{fmtMoney(e.total_cost)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function Registers() {
  const { activeProjectName } = useActiveProjectContext();
  const [objectName, setObjectName] = useState(activeProjectName ?? '');
  const [uchastok, setUchastok] = useState('');
  const [title, setTitle] = useState('');
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([]);
  const [ks2Files, setKs2Files] = useState<File[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [poll, setPoll] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { can_write } = useAgentPermission('smeta');
  const currentUserId = useAppSelector((s) => s.auth.user?.id);

  useEffect(() => {
    if (activeProjectName && !objectName) {
      setObjectName(activeProjectName);
    }
  }, [activeProjectName, objectName]);

  const listQ = useListRegistersQuery(undefined, { pollingInterval: 5000 });
  const [createRegister, { isLoading: creating }] = useCreateRegisterMutation();
  const [deleteRegister] = useDeleteRegisterMutation();
  const detailQ = useGetRegisterQuery(selectedId ?? '', {
    skip: !selectedId,
    pollingInterval: poll,
  });
  const [exportXlsx, { isFetching: exporting }] = useLazyExportRegisterXlsxQuery();
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedId) {
      setPoll(0);
      return;
    }
    const st = detailQ.data?.session.status;
    setPoll(st === 'pending' || st === 'processing' ? 2500 : 0);
  }, [selectedId, detailQ.data?.session.status]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (invoiceFiles.length === 0 && ks2Files.length === 0) {
      setError('Приложите хотя бы один файл — накладную или акт КС-2.');
      return;
    }
    const fd = new FormData();
    fd.append('object_name', objectName);
    if (uchastok.trim()) fd.append('uchastok', uchastok);
    if (title.trim()) fd.append('title', title);
    invoiceFiles.forEach((f) => fd.append('invoice', f));
    ks2Files.forEach((f) => fd.append('ks2', f));
    try {
      const res = await createRegister(fd).unwrap();
      setSelectedId(res.session_id);
      setObjectName('');
      setUchastok('');
      setTitle('');
      setInvoiceFiles([]);
      setKs2Files([]);
    } catch (err) {
      setError(
        apiErrorText(err, 'Не удалось запустить построение реестра. Проверьте формат файлов.'),
      );
      // eslint-disable-next-line no-console
      console.error('createRegister failed', err);
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteRegister(id).unwrap();
      if (selectedId === id) setSelectedId(null);
    } catch {
      // ignore — список обновится при следующем поллинге
    }
  }

  async function handleExport(id: string) {
    setExportError(null);
    try {
      const blob = await exportXlsx(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `register_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError('Не удалось выгрузить реестр XLSX.');
      // eslint-disable-next-line no-console
      console.error('exportRegisterXlsx failed', err);
    }
  }

  const sessions = listQ.data?.items ?? [];
  const detail = detailQ.data;
  const rawSessionWarnings = (detail?.session.summary as Record<string, unknown> | null)?.[
    'warnings'
  ];
  const sessionWarnings = Array.isArray(rawSessionWarnings)
    ? rawSessionWarnings.filter((x): x is string => typeof x === 'string')
    : [];
  const isAuthor =
    !detail?.session.user_id ||
    !currentUserId ||
    detail.session.user_id === currentUserId;

  return (
    <Stack>
      <Row>
        <Muted>
          Самодостаточные реестры по пакету первички за период (по месяцам/участкам).
        </Muted>
        <CrossLink to="/agents/smeta/accumulations">
          Накопительный учёт ПА (лента по договору) →
        </CrossLink>
      </Row>
      <Card>
        <form onSubmit={handleSubmit}>
          <Field>
            <Label>Объект</Label>
            <Input
              placeholder="Например: Завод ГБЖ"
              value={objectName}
              onChange={(e) => setObjectName(e.target.value)}
            />
          </Field>
          <Field>
            <Label>Участок (опционально)</Label>
            <Input
              placeholder="Например: Корпус КЖ"
              value={uchastok}
              onChange={(e) => setUchastok(e.target.value)}
            />
          </Field>
          <Field>
            <Label>Заголовок реестра (опционально)</Label>
            <Input
              placeholder="Например: Накопительный реестр за II квартал 2026"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field>
            <Label>Накладные (Excel)</Label>
            <Input
              type="file"
              multiple
              accept=".xlsx,.xlsm"
              onChange={(e) => setInvoiceFiles(Array.from(e.target.files ?? []))}
            />
            <FileHint>
              ТОРГ-12 / УПД / реестры в .xlsx. Выбрано файлов: {invoiceFiles.length}
            </FileHint>
          </Field>
          <Field>
            <Label>Акты КС-2 (Excel)</Label>
            <Input
              type="file"
              multiple
              accept=".xlsx,.xlsm"
              onChange={(e) => setKs2Files(Array.from(e.target.files ?? []))}
            />
            <FileHint>Excel-выгрузка КС-2. Выбрано файлов: {ks2Files.length}</FileHint>
          </Field>
          {error && <Err>{error}</Err>}
          <SubmitWrap>
            <Button
              type="submit"
              $variant="primary"
              disabled={creating || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {creating ? 'Запуск…' : 'Построить реестр'}
            </Button>
          </SubmitWrap>
        </form>
      </Card>

      <Grid>
        <Stack>
          <Muted>Реестров: {sessions.length}</Muted>
          {sessions.map((s) => {
            const { failedSources: rowFailed } = extractFailedSources(s.summary);
            return (
              <CheckRow key={s.id} $active={s.id === selectedId} onClick={() => setSelectedId(s.id)}>
                <Row>
                  <ObjName>{s.title || s.object_name}</ObjName>
                  <Badge $tone={statusTone(s.status)}>{statusLabel(s.status)}</Badge>
                </Row>
                <Row>
                  <Muted>
                    {s.uchastok ? `${s.uchastok} · ` : ''}
                    {new Date(s.created_at).toLocaleString('ru-RU')}
                  </Muted>
                  {s.status === 'processing' && <Muted>{s.progress_percent}%</Muted>}
                  {rowFailed.length > 0 && (
                    <Badge $tone="warning">сбоев чтения: {rowFailed.length}</Badge>
                  )}
                </Row>
              </CheckRow>
            );
          })}
          {sessions.length === 0 && (
            <Muted>Пока нет реестров. Загрузите накладные и акты выше.</Muted>
          )}
        </Stack>

        <div>
          {!selectedId && <Muted>Выберите реестр слева, чтобы увидеть позиции.</Muted>}
          {selectedId && !detail && <Muted>Загрузка…</Muted>}
          {detail && (() => {
            const { failedSources, totalFiles } = extractFailedSources(detail.session.summary);
            return (
              <Stack>
                <ParseWarningsBanner warnings={sessionWarnings} />
                <Card>
                <Row>
                  <div>
                    <ObjName>{detail.session.title || detail.session.object_name}</ObjName>
                    <Muted>
                      {detail.session.object_name}
                      {detail.session.uchastok ? ` · ${detail.session.uchastok}` : ''} ·
                      источников: {detail.sources.length} · накладные:{' '}
                      {detail.invoice_entries.length} · акты: {detail.act_entries.length}
                    </Muted>
                  </div>
                  <Row>
                    <Badge $tone={statusTone(detail.session.status)}>
                      {statusLabel(detail.session.status)}
                    </Badge>
                    {detail.session.status === 'completed' && (
                      <Button
                        $variant="secondary"
                        $size="sm"
                        type="button"
                        onClick={() => handleExport(detail.session.id)}
                        disabled={exporting}
                      >
                        {exporting ? 'Готовлю…' : 'Сформировать реестр (XLSX)'}
                      </Button>
                    )}
                    <Button
                      $variant="ghost"
                      $size="sm"
                      type="button"
                      disabled={!can_write || !isAuthor}
                      title={
                        !can_write
                          ? 'Только чтение'
                          : !isAuthor
                            ? 'Удалить реестр может только его автор'
                            : undefined
                      }
                      onClick={() => handleDelete(detail.session.id)}
                    >
                      Удалить
                    </Button>
                  </Row>
                </Row>

                {failedSources.length > 0 && (
                  <FailedSourcesBanner role="alert">
                    <FailedSourcesTitle>
                      Не удалось разобрать файлов: {failedSources.length}
                      {totalFiles !== null ? ` из ${totalFiles}` : ''}. Реестр построен только по
                      остальным документам — перепроверьте перечисленные источники:
                    </FailedSourcesTitle>
                    <FailedSourcesList>
                      {failedSources.map((src, idx) => (
                        <li key={`${src}-${idx}`}>{src}</li>
                      ))}
                    </FailedSourcesList>
                  </FailedSourcesBanner>
                )}

                {detail.session.status === 'failed' && detail.session.error_message && (
                  <Err $mt={8}>{detail.session.error_message}</Err>
                )}
                {exportError && <Err $mt={8}>{exportError}</Err>}

                <TableTitle>
                  Реестр накладных <Badge $tone="neutral">{detail.invoice_entries.length}</Badge>
                </TableTitle>
                <InvoiceTable entries={detail.invoice_entries} />

                <TableTitle>
                  Реестр актов КС-2 <Badge $tone="neutral">{detail.act_entries.length}</Badge>
                </TableTitle>
                <ActTable entries={detail.act_entries} />
                </Card>
              </Stack>
            );
          })()}
        </div>
      </Grid>
    </Stack>
  );
}
